import Link from 'next/link'
import { and, asc, desc, eq, sql } from 'drizzle-orm'
import { FieldLabel, LabelWithTooltip, PrimaryButton, SelectField } from '@/app/FormFields'
import { db } from '@/db'
import { projects, ticketMessages, tickets, users, type TicketStatus } from '@/db/schema'
import { requireExpertUser } from '@/lib/role-guard'
import { loadTenantConfig } from '@/lib/tenant-config-loader'
import { buildInboxTicketTitle } from '@/lib/inbox-ticket-display'
import { formatDateTimeInUserZone } from '@/lib/user-time-zone'
import InboxEvents from './InboxEvents'

const CONFIG_PATH = process.env.TENANT_CONFIG_PATH ?? './tenant.config.yaml'

const STATUS_LABELS: Record<string, string> = {
  SUBMITTED: 'Awaiting AI triage',
  NEEDS_INFO: 'Needs info',
  TRIAGED: 'Triaged',
  AWAITING_EXPERT: 'Ready for review',
  EXPERT_REVIEWING: 'In expert review',
  RESPONSE_DRAFTING: 'Drafting',
  RESPONSE_READY: 'Response ready',
  RESPONDED: 'Responded',
  CLOSED: 'Closed',
}

const PRIORITY_LABELS: Record<string, string> = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
}

const PRIORITY_BADGE_CLASSES: Record<string, string> = {
  critical: 'border-red-300 bg-red-50 text-red-800 dark:border-red-400/40 dark:bg-red-400/10 dark:text-red-200',
  high: 'border-orange-300 bg-orange-50 text-orange-800 dark:border-orange-400/40 dark:bg-orange-400/10 dark:text-orange-200',
  medium: 'border-yellow-300 bg-yellow-50 text-yellow-800 dark:border-yellow-400/40 dark:bg-yellow-400/10 dark:text-yellow-100',
  low: 'border-zinc-300 bg-zinc-50 text-zinc-700 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-300',
}

const PRIORITY_SORT_RANK = sql<number>`
  case ${tickets.userPriority}
    when 'critical' then 4
    when 'high' then 3
    when 'medium' then 2
    when 'low' then 1
    else 0
  end
`

function firstParam(value: string | string[] | undefined): string {
  return Array.isArray(value) ? value[0] ?? '' : value ?? ''
}

export default async function InboxPage({
  searchParams,
}: {
  searchParams: Promise<Record<string, string | string[] | undefined>>
}) {
  const expert = await requireExpertUser()
  const params = await searchParams
  const selectedProject = firstParam(params.project)
  const selectedCategory = firstParam(params.category)
  const selectedPriority = firstParam(params.priority)
  const selectedStatus = firstParam(params.status)
  const selectedSort = firstParam(params.sort) || 'submitted'
  const tenantConfig = loadTenantConfig(CONFIG_PATH)

  const [currentUser] = await db
    .select({ timeZone: users.timeZone })
    .from(users)
    .where(eq(users.id, expert.id))
    .limit(1)

  const projectOptions = await db
    .select({ id: projects.id, name: projects.name })
    .from(projects)
    .where(and(eq(projects.tenantId, expert.tenantId), eq(projects.isActive, true)))
    .orderBy(asc(projects.name))

  const filters = [
    eq(tickets.tenantId, expert.tenantId),
    selectedProject ? eq(tickets.projectId, selectedProject) : undefined,
    selectedCategory ? eq(tickets.issueCategory, selectedCategory) : undefined,
    selectedPriority ? eq(tickets.userPriority, selectedPriority as 'critical' | 'high' | 'medium' | 'low') : undefined,
    selectedStatus ? eq(tickets.status, selectedStatus as TicketStatus) : undefined,
  ].filter(Boolean)

  const orderBy =
    selectedSort === 'occurrence'
      ? [desc(tickets.dateOfOccurrence), desc(tickets.createdAt)]
      : selectedSort === 'priority'
        ? [desc(PRIORITY_SORT_RANK), desc(tickets.createdAt)]
        : [desc(tickets.createdAt)]

  const rows = await db
    .select({
      id: tickets.id,
      title: tickets.title,
      referenceNumber: tickets.referenceNumber,
      status: tickets.status,
      createdAt: tickets.createdAt,
      updatedAt: tickets.updatedAt,
      projectName: projects.name,
      dateOfOccurrence: tickets.dateOfOccurrence,
      issueCategory: tickets.issueCategory,
      affectedSystem: tickets.affectedSystem,
      userPriority: tickets.userPriority,
      submitterName: users.displayName,
      submitterEmail: users.email,
      lastMessage: ticketMessages.bodySanitized,
    })
    .from(tickets)
    .innerJoin(users, eq(users.id, tickets.submitterId))
    .leftJoin(projects, eq(projects.id, tickets.projectId))
    .leftJoin(ticketMessages, eq(ticketMessages.id, tickets.lastMessageId))
    .where(and(...filters))
    .orderBy(...orderBy)

  return (
    <main className="min-h-screen bg-zinc-50 px-4 py-10 text-zinc-950 dark:bg-zinc-950 dark:text-zinc-100">
      <InboxEvents />
      <div className="mx-auto max-w-5xl space-y-6">
        <div className="flex items-end justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-zinc-950 dark:text-white">Expert Inbox</h1>
            <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">Newly triaged tickets appear here as they clear AI review.</p>
          </div>
          <span className="rounded border border-zinc-200 bg-white px-3 py-1 text-sm text-zinc-700 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-300">
            {rows.length} shown
          </span>
        </div>

        <form className="grid gap-3 rounded-lg border border-zinc-200 bg-white p-4 md:grid-cols-5 dark:border-zinc-800 dark:bg-zinc-900">
          <div className="space-y-2">
            <FieldLabel htmlFor="filter-project" className="text-xs">Project</FieldLabel>
            <SelectField
              id="filter-project"
              name="project"
              defaultValue={selectedProject}
              className="h-11 text-sm"
            >
              <option value="">All projects</option>
              {projectOptions.map((project) => (
                <option key={project.id} value={project.id}>{project.name}</option>
              ))}
            </SelectField>
          </div>
          <div className="space-y-2">
            <FieldLabel htmlFor="filter-category" className="text-xs">Category</FieldLabel>
            <SelectField
              id="filter-category"
              name="category"
              defaultValue={selectedCategory}
              className="h-11 text-sm"
            >
              <option value="">All categories</option>
              {tenantConfig.intake.issue_categories.map((category) => (
                <option key={category} value={category}>{category}</option>
              ))}
            </SelectField>
          </div>
          <div className="space-y-2">
            <LabelWithTooltip
              htmlFor="filter-priority"
              className="text-xs"
              tooltipId="filter-priority-help"
              tooltipText="Priority is selected by the line manager at submission time. Sorting by priority puts critical and high-priority tickets first."
            >
              Priority
            </LabelWithTooltip>
            <SelectField
              id="filter-priority"
              name="priority"
              defaultValue={selectedPriority}
              className="h-11 text-sm"
              aria-describedby="filter-priority-help"
            >
              <option value="">All priorities</option>
              {Object.entries(PRIORITY_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </SelectField>
          </div>
          <div className="space-y-2">
            <LabelWithTooltip
              htmlFor="filter-status"
              className="text-xs"
              tooltipId="filter-status-help"
              tooltipText="Status shows where the ticket is in the workflow, from AI triage through expert review, response, follow-up, and close."
            >
              Status
            </LabelWithTooltip>
            <SelectField
              id="filter-status"
              name="status"
              defaultValue={selectedStatus}
              className="h-11 text-sm"
              aria-describedby="filter-status-help"
            >
              <option value="">All statuses</option>
              {Object.entries(STATUS_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </SelectField>
          </div>
          <div className="space-y-2">
            <LabelWithTooltip
              htmlFor="filter-sort"
              className="text-xs"
              tooltipId="filter-sort-help"
              tooltipText="Choose whether the inbox is ordered by when the ticket was submitted, when the issue happened, or the priority reported by the line manager."
            >
              Sort
            </LabelWithTooltip>
            <SelectField
              id="filter-sort"
              name="sort"
              defaultValue={selectedSort}
              className="h-11 text-sm"
              aria-describedby="filter-sort-help"
            >
              <option value="submitted">Date submitted</option>
              <option value="occurrence">Date of occurrence</option>
              <option value="priority">User priority</option>
            </SelectField>
          </div>
          <div className="flex items-end gap-2 md:col-span-5">
            <PrimaryButton
              type="submit"
              className="py-2.5"
            >
              Apply filters
            </PrimaryButton>
            <Link
              href="/inbox"
              className="rounded-xl border border-zinc-200 bg-white px-3 py-2.5 text-sm font-medium text-zinc-800 transition hover:bg-zinc-100 dark:border-zinc-700 dark:bg-zinc-950 dark:text-zinc-200 dark:hover:bg-zinc-800"
            >
              Reset
            </Link>
          </div>
        </form>

        <div className="overflow-hidden rounded-lg border border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900">
          {rows.length === 0 ? (
            <div className="px-6 py-12 text-center">
              <h2 className="text-sm font-medium text-zinc-950 dark:text-white">Inbox clear</h2>
              <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">Tickets will appear as soon as summarization finishes.</p>
            </div>
          ) : (
            <ul className="divide-y divide-zinc-200 dark:divide-zinc-800">
              {rows.map((ticket) => (
                <li key={ticket.id}>
                  <Link
                    href={`/tickets/${ticket.id}`}
                    className="grid gap-3 px-5 py-4 transition hover:bg-zinc-100 md:grid-cols-[1fr_auto] md:items-center dark:hover:bg-zinc-800/70"
                  >
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-xs font-mono text-zinc-500 dark:text-zinc-400">{ticket.referenceNumber}</p>
                        {ticket.issueCategory && (
                          <span className="rounded border border-sky-200 bg-sky-50 px-2 py-0.5 text-xs font-medium text-sky-800 dark:border-sky-400/30 dark:bg-sky-400/10 dark:text-sky-200">
                            {ticket.issueCategory}
                          </span>
                        )}
                        {ticket.userPriority && (
                          <span
                            data-testid={`priority-badge-${ticket.userPriority}`}
                            className={`rounded border px-2 py-0.5 text-xs font-semibold ${PRIORITY_BADGE_CLASSES[ticket.userPriority] ?? PRIORITY_BADGE_CLASSES.low}`}
                          >
                            {PRIORITY_LABELS[ticket.userPriority] ?? ticket.userPriority}
                          </span>
                        )}
                        <span className="rounded border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-800 dark:border-emerald-400/30 dark:bg-emerald-400/10 dark:text-emerald-200">
                          {STATUS_LABELS[ticket.status] ?? ticket.status}
                        </span>
                      </div>
                      <h2 className="mt-1 truncate text-base font-semibold text-zinc-950 dark:text-white">
                        {buildInboxTicketTitle(ticket)}
                      </h2>
                      <p className="mt-1 truncate text-sm text-zinc-600 dark:text-zinc-400">
                        {ticket.submitterName ?? ticket.submitterEmail}
                        {ticket.projectName ? ` · ${ticket.projectName}` : ''}
                        {ticket.lastMessage ? ` · ${ticket.lastMessage}` : ''}
                      </p>
                    </div>
                    <time className="text-sm text-zinc-600 dark:text-zinc-400" dateTime={ticket.createdAt.toISOString()}>
                      {formatDateTimeInUserZone(ticket.createdAt, currentUser?.timeZone)}
                    </time>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </main>
  )
}
